
import React, { useState, useMemo } from 'react';
import { Icons } from '../ui/Icons';
import { ReportPermissionLevel, AttendanceRecord, StaffMember } from '../../types';
import { safeFormat } from '../../utils/safeDateFns';

interface AttendanceReportProps {
    attendanceRecords?: AttendanceRecord[];
    staffList?: StaffMember[];
    permissionLevel?: ReportPermissionLevel;
    initialLevel?: 'form' | 'preview' | 'history';
    onLevelChange?: (level: 'form' | 'preview' | 'history') => void;
}

const AttendanceReport: React.FC<AttendanceReportProps> = ({ 
    attendanceRecords = [],
    staffList = [],
    permissionLevel = 'view',
    initialLevel = 'form',
    onLevelChange
}) => {
    const canExport = permissionLevel === 'export';

    const [currentLevel, setCurrentLevel] = useState<'form' | 'preview' | 'history'>(initialLevel);
    const [startDate, setStartDate] = useState(safeFormat(new Date(Date.now() - 7 * 86400000), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(safeFormat(new Date(), 'yyyy-MM-dd'));

    const handleLevelChange = (level: 'form' | 'preview' | 'history') => {
        setCurrentLevel(level);
        if (onLevelChange) onLevelChange(level);
    };

    const filteredAttendance = useMemo(() => {
        return attendanceRecords.filter(record => {
            const date = record.date;
            if (!date) return false;
            const resDate = new Date(date).getTime();
            const selStart = new Date(startDate).getTime();
            const selEnd = new Date(endDate).getTime();
            return resDate >= selStart && resDate <= selEnd;
        });
    }, [attendanceRecords, startDate, endDate]);

    const stats = useMemo(() => {
        const total = filteredAttendance.length;
        const missing = filteredAttendance.filter(r => !r.actualStartTime).length;
        const complete = total - missing;
        return { total, complete, missing };
    }, [filteredAttendance]);

    const renderForm = () => (
        <div className="max-w-xl mx-auto">
            <div className="bg-white dark:bg-slate-900 p-8 rounded-[40px] shadow-sm border border-gray-100 dark:border-slate-800 space-y-8">
                <div className="flex items-center gap-4 mb-2">
                    <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-900/30 rounded-2xl flex items-center justify-center text-indigo-600">
                        <Icons.UserCheck className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tight">Attendance Analysis</h3>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Select Attendance Period</p>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 px-1">Start Date</label>
                        <input 
                            type="date" 
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full bg-gray-50 dark:bg-slate-800 border-none rounded-2xl p-4 font-bold text-sm focus:ring-2 focus:ring-indigo-500 transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 px-1">End Date</label>
                        <input 
                            type="date" 
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full bg-gray-50 dark:bg-slate-800 border-none rounded-2xl p-4 font-bold text-sm focus:ring-2 focus:ring-indigo-500 transition-all"
                        />
                    </div>
                </div>

                <button 
                    onClick={() => handleLevelChange('preview')}
                    className="w-full bg-indigo-600 text-white py-6 rounded-[32px] font-black uppercase tracking-widest shadow-2xl shadow-indigo-200 dark:shadow-none hover:bg-indigo-700 transition-all flex items-center justify-center gap-3 group"
                >
                    Generate Report
                    <Icons.ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
            </div>
        </div>
    );

    const renderPreview = () => (
        <div className="space-y-6">
            <div className="bg-white dark:bg-slate-900 p-6 rounded-[32px] border border-gray-100 dark:border-slate-800 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 no-print">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => handleLevelChange('form')}
                        className="w-10 h-10 rounded-full bg-gray-50 dark:bg-slate-800 flex items-center justify-center hover:bg-gray-100 transition-colors"
                    >
                        <Icons.ArrowLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <div>
                        <h2 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tight">Attendance Report</h2>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{startDate} — {endDate}</p>
                    </div>
                </div>

                {canExport && (
                    <button 
                        onClick={() => window.print()}
                        className="px-6 py-2 bg-gray-900 text-white rounded-xl text-xs font-bold hover:opacity-90 transition-all flex items-center gap-2"
                    >
                        <Icons.Printer className="w-4 h-4" /> Print Report
                    </button>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 no-print">
                <div className="bg-white dark:bg-slate-900 p-6 rounded-[32px] border border-gray-100 dark:border-slate-800 flex items-center gap-4">
                    <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-900/30 rounded-2xl flex items-center justify-center text-indigo-600">
                        <Icons.Users className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="text-2xl font-black text-gray-900 dark:text-white">{stats.total}</div>
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Records</div>
                    </div>
                </div>
                <div className="bg-white dark:bg-slate-900 p-6 rounded-[32px] border border-gray-100 dark:border-slate-800 flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-900/30 rounded-2xl flex items-center justify-center text-emerald-600">
                        <Icons.CheckCircle className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="text-2xl font-black text-gray-900 dark:text-white">{stats.complete}</div>
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Completed Clockings</div>
                    </div>
                </div>
                <div className="bg-white dark:bg-slate-900 p-6 rounded-[32px] border border-gray-100 dark:border-slate-800 flex items-center gap-4">
                    <div className="w-12 h-12 bg-rose-50 dark:bg-rose-900/30 rounded-2xl flex items-center justify-center text-rose-600">
                        <Icons.XCircle className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="text-2xl font-black text-gray-900 dark:text-white">{stats.missing}</div>
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Missing Clock-ins</div>
                    </div>
                </div>
            </div>

            <div className="bg-white p-8 rounded-[40px] shadow-sm border border-gray-200 overflow-x-auto">
                <table className="w-full text-left text-[11px] border-collapse">
                    <thead>
                        <tr className="bg-gray-50 uppercase tracking-widest text-gray-500 font-black border-b border-gray-100">
                            <th className="px-4 py-4">Staff Member</th>
                            <th className="px-4 py-4">Date</th>
                            <th className="px-4 py-4">Shift</th>
                            <th className="px-4 py-4">Clock In</th>
                            <th className="px-4 py-4">Clock Out</th>
                            <th className="px-4 py-4">Manual Adj.</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {filteredAttendance.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="px-4 py-12 text-center text-gray-400 font-bold uppercase tracking-widest">No attendance records found for this period</td>
                            </tr>
                        ) : (
                            filteredAttendance.map(record => {
                                const staffMember = staffList.find(s => s.id === record.employeeId);
                                return (
                                    <tr key={record.id}>
                                        <td className="px-4 py-4 font-bold text-gray-900">{staffMember?.name || 'Unknown Staff'}</td>
                                        <td className="px-4 py-4">{record.date}</td>
                                        <td className="px-4 py-4 uppercase font-bold text-gray-400">{record.shiftId || 'Flexible'}</td>
                                        <td className="px-4 py-4 font-black text-indigo-600">{record.actualStartTime || '--:--'}</td>
                                        <td className="px-4 py-4 font-black text-gray-900">{record.actualEndTime || '--:--'}</td>
                                        <td className="px-4 py-4">
                                            <span className="text-gray-300">System</span>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            {currentLevel === 'form' ? renderForm() : renderPreview()}
        </div>
    );
};

export default AttendanceReport;
